import { useMemo } from 'react';
import type { Store } from '@/shared/config/app-config';
import { CheckboxTree } from '@/shared/ui/checkbox-tree/CheckboxTree';

interface AssignedStoresFieldProps {
  stores: readonly Store[];
  selectedIds: readonly string[];
  onChange: (ids: string[]) => void;
  readOnly?: boolean;
  /** The user dialog keeps the frame but hides the tree for the "workshop" role. */
  hideTree?: boolean;
  /**
   * No bordered, capped frame around the tree (the background dialog shows
   * it as a plain block).
   */
  frameless?: boolean;
  testId?: string | undefined;
}

/** "Assigned stores" column of the screensaver, background and user dialogs. */
export function AssignedStoresField({
  stores,
  selectedIds,
  onChange,
  readOnly = false,
  hideTree = false,
  frameless = false,
  testId,
}: Readonly<AssignedStoresFieldProps>) {
  const rows = useMemo(
    () =>
      [...stores]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((store) => ({
          id: String(store.id),
          region: store.region,
          country: store.country,
          label: store.name,
        })),
    [stores],
  );
  return (
    <div className={frameless ? undefined : 'store-list'}>
      <span className="field-label">Assigned stores</span>
      <div
        className={
          frameless ? undefined : 'checkbox-group checkbox-group-capped'
        }
        role="region"
        aria-label="Assigned stores"
      >
        {!hideTree && (
          <CheckboxTree
            rows={rows}
            breakdown={['region', 'country']}
            idKey="id"
            labelKey="label"
            selectedIds={selectedIds}
            onChange={(ids) => onChange(ids.map(String))}
            readOnly={readOnly}
            testId={testId}
          />
        )}
      </div>
    </div>
  );
}
